import { useRef, useEffect } from "react";

// Lightweight animated starfield: drifting + twinkling stars on canvas.
export default function Starfield() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let w, h, dpr;
    let stars = [];
    const mouse = { x: 0, y: 0 };

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.min(120, Math.floor((w * h) / 14000));
      stars = Array.from({ length: count }, () => {
        const depth = Math.random();
        return {
          x: Math.random() * w,
          y: Math.random() * h,
          r: depth * 1.6 + 0.3,
          depth,
          tw: Math.random() * Math.PI * 2,
          tws: Math.random() * 0.02 + 0.005,
          vy: depth * 0.08 + 0.02,
          hue: Math.random() < 0.15 ? "gold" : Math.random() < 0.3 ? "violet" : "white",
        };
      });
    };

    const color = (hue, a) => {
      if (hue === "gold") return `rgba(240,224,160,${a})`;
      if (hue === "violet") return `rgba(178,150,255,${a})`;
      return `rgba(255,255,255,${a})`;
    };

    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      for (const s of stars) {
        s.tw += s.tws;
        s.y += s.vy;
        if (s.y > h + 2) s.y = -2;
        const twinkle = 0.45 + Math.abs(Math.sin(s.tw)) * 0.55;
        const px = s.x + (mouse.x * s.depth * 22);
        const py = s.y + (mouse.y * s.depth * 22);
        ctx.beginPath();
        ctx.arc(px, py, s.r, 0, Math.PI * 2);
        ctx.fillStyle = color(s.hue, twinkle);
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };

    const onMove = (e) => {
      mouse.x = (e.clientX / window.innerWidth - 0.5) * 2;
      mouse.y = (e.clientY / window.innerHeight - 0.5) * 2;
    };

    resize();
    draw();
    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", onMove);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      data-testid="starfield"
      className="fixed inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
}
